import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';

interface Transaction {
  id: number;
  transaction_type: string;
  amount_pounds: string;
  balance_after_pounds: string;
  description: string;
  reference_type: string;
  reference_id: string;
  created_at: string;
}

const WalletPageUpdated = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'all' | 'topups' | 'deductions'>('all');
  const [availableBalance, setAvailableBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);

  const features = [
    {
      icon: '⚡',
      title: 'Pre-fund for faster booking',
      description:
        'Skip payment processing at checkout and secure your concierge service instantly.',
    },
    {
      icon: '⚙️',
      title: 'Instant payment on urgent services',
      description:
        'Our team can deploy specialists immediately when funds are pre-authorized.',
    },
    {
      icon: '🏢',
      title: 'Works across all properties',
      description:
        'A unified balance for maintenance, cleaning, and security across your portfolio.',
    },
  ];

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
  }, []);

  const fetchBalance = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/wallet/balance');
      if (res?.data) {
        setAvailableBalance(parseFloat(res.data.balance_pounds ?? res.data.balance ?? '0'));
      } else {
        setAvailableBalance(0);
      }
    } catch {
      // Fallback to customer wallet endpoint
      try {
        const res2 = await apiService.get('/customer/wallet');
        if (res2?.data) {
          setAvailableBalance(parseFloat(res2.data.balance ?? res2.data.availableBalance ?? '0'));
        } else {
          setAvailableBalance(0);
        }
      } catch {
        setAvailableBalance(0);
        toast({ title: 'Error', description: 'Failed to load wallet balance', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setTxLoading(true);
    try {
      const res = await apiService.get('/wallet/transactions');
      if (res?.data && Array.isArray(res.data)) {
        setTransactions(res.data);
      } else {
        setTransactions([]);
      }
    } catch {
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  const isCredit = (t: Transaction) =>
    t.transaction_type === 'credit' || t.transaction_type === 'topup' || t.transaction_type === 'refund';

  const filteredTransactions = transactions.filter((t) => {
    if (activeTab === 'topups') return isCredit(t);
    if (activeTab === 'deductions') return !isCredit(t);
    return true;
  });

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <DashboardLayout>
      <Helmet>
        <title>Wallet | Virewire</title>
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wallet</h1>
            <p className="text-gray-600 mt-2">
              Manage your account balance and transaction history
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <Card key={idx} className="border-1 bg-teal-50 border-teal-200">
              <CardContent className="p-6">
                <div className="text-3xl mb-3">{feature.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-700">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Balance Section */}
        <Card className="border-1 bg-gradient-to-br from-green-700 to-green-800 text-white">
          <CardContent className="p-8">
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide mb-2 opacity-90">
                  • Available Balance
                </p>
                {loading ? (
                  <Loader2 className="w-8 h-8 animate-spin opacity-70 mt-2" />
                ) : (
                  <div className="flex items-baseline gap-2">
                    <p className="text-4xl font-bold">
                      £{availableBalance !== null ? availableBalance.toFixed(2) : '0.00'}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center border-l border-white border-opacity-20">
                <Button
                  onClick={() => navigate('/wallet/add-funds')}
                  className="bg-white text-green-700 hover:bg-gray-100 font-semibold flex items-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Add Funds
                </Button>
              </div>

              <div className="flex items-center justify-center border-l border-white border-opacity-20">
                <button
                  className="text-white font-semibold hover:underline text-center"
                  onClick={() => document.getElementById('tx-history')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  View Transactions
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-3 gap-8">
          {/* Auto Top-up */}
          <Card className="border-1">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="text-2xl flex-shrink-0 mt-1">⚡</span>
                <div>
                  <h3 className="font-bold text-gray-900">Smart Auto Top-Up</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Automatically top up your balance when it runs low to ensure no service interruptions.
                  </p>
                </div>
              </div>
              <button className="text-green-700 font-semibold text-sm hover:underline">
                Configure settings →
              </button>
            </CardContent>
          </Card>

          {/* Portfolio Manager */}
          <Card className="border-1 col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="text-4xl">💼</div>
                <div>
                  <h3 className="font-bold text-lg mb-1">
                    Need a dedicated portfolio manager?
                  </h3>
                  <p className="text-sm text-gray-300">
                    Upgrade to Elite Concierge
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction History */}
        <Card className="border-1" id="tx-history">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Transaction History</h2>
              <div className="flex gap-3">
                {['All Activity', 'Top-ups', 'Deductions'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() =>
                      setActiveTab(
                        tab === 'All Activity'
                          ? 'all'
                          : tab === 'Top-ups'
                          ? 'topups'
                          : 'deductions'
                      )
                    }
                    className={`px-3 py-1 text-sm font-semibold rounded-lg transition-colors ${
                      (activeTab === 'all' && tab === 'All Activity') ||
                      (activeTab === 'topups' && tab === 'Top-ups') ||
                      (activeTab === 'deductions' && tab === 'Deductions')
                        ? 'bg-green-700 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {txLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-green-700" />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No transactions yet</p>
                <p className="text-sm text-gray-400 mt-1">Your transaction history will appear here</p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-xs font-bold text-gray-600 uppercase px-4 py-3 text-left">
                          Trans ID
                        </th>
                        <th className="text-xs font-bold text-gray-600 uppercase px-4 py-3 text-left">
                          Date
                        </th>
                        <th className="text-xs font-bold text-gray-600 uppercase px-4 py-3 text-left">
                          Description
                        </th>
                        <th className="text-xs font-bold text-gray-600 uppercase px-4 py-3 text-left">
                          Type
                        </th>
                        <th className="text-xs font-bold text-gray-600 uppercase px-4 py-3 text-right">
                          Amount
                        </th>
                        <th className="text-xs font-bold text-gray-600 uppercase px-4 py-3 text-right">
                          Balance After
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((t) => {
                        const credit = isCredit(t);
                        return (
                          <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <p className="text-sm font-mono text-gray-600">TXN-{t.id}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-700">{formatDate(t.created_at)}</p>
                            </td>
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-semibold text-gray-900">{t.description || 'Transaction'}</p>
                                {(t.reference_type || t.reference_id) && (
                                  <p className="text-xs text-gray-500">
                                    {[t.reference_type, t.reference_id].filter(Boolean).join(' · ')}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  credit
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {credit ? '• CREDIT' : '• DEBIT'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <p className={`font-semibold ${credit ? 'text-green-600' : 'text-red-600'}`}>
                                {credit ? '+' : '-'}£{parseFloat(t.amount_pounds).toFixed(2)}
                              </p>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <p className="text-sm text-gray-600">
                                £{parseFloat(t.balance_after_pounds).toFixed(2)}
                              </p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    Showing {filteredTransactions.length} of {transactions.length} transactions
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default WalletPageUpdated;
